/**
 * 問い合わせドメイン型定義
 */

// ============================================
// カテゴリ
// ============================================

/** 問い合わせカテゴリ */
export type ContactCategory = "bug" | "feature" | "feedback" | "other";

/** カテゴリ表示名マッピング */
export const CONTACT_CATEGORY_LABELS: Record<ContactCategory, string> = {
  bug: "バグ報告",
  feature: "機能要望",
  feedback: "フィードバック",
  other: "その他",
};

// ============================================
// リクエスト/レスポンス
// ============================================

/** 問い合わせリクエスト */
export interface ContactRequest {
  /** カテゴリ */
  category: ContactCategory;
  /** メールアドレス */
  email: string;
  /** 名前（任意） */
  name?: string;
  /** 件名 */
  subject: string;
  /** 本文 */
  message: string;
}

/** 問い合わせ成功レスポンス */
export interface ContactResponse {
  success: true;
  message: string;
  contactId: string;
}

// ============================================
// 保存レコード
// ============================================

/** 問い合わせステータス */
export type ContactStatus = "pending" | "reviewed" | "resolved";

/** ステータス表示名マッピング */
export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  pending: "未対応",
  reviewed: "確認済み",
  resolved: "解決済み",
};

// ============================================
// 対応履歴
// ============================================

/** 対応アクション種別 */
export type ContactActionType = "status_changed" | "note_added" | "reply_sent";

/** ステータス変更詳細 */
export interface StatusChangedDetails {
  type: "status_changed";
  from: ContactStatus;
  to: ContactStatus;
  note?: string;
}

/** メモ追加詳細 */
export interface NoteAddedDetails {
  type: "note_added";
  content: string;
}

/** 返信送信詳細 */
export interface ReplySentDetails {
  type: "reply_sent";
  subject: string;
  message: string;
}

/** アクション詳細（union型） */
export type ContactActionDetails = StatusChangedDetails | NoteAddedDetails | ReplySentDetails;

/** 対応履歴エントリ */
export interface ContactActionEntry {
  /** アクションID（UUID） */
  id: string;
  /** アクション種別 */
  type: ContactActionType;
  /** 実行者（管理者メール） */
  performedBy: string;
  /** 実行日時（ISO 8601） */
  performedAt: string;
  /** アクション詳細 */
  details: ContactActionDetails;
}

// ============================================
// 保存レコード
// ============================================

/** 問い合わせ保存レコード */
export interface ContactRecord {
  /** 問い合わせID（UUID） */
  id: string;
  /** カテゴリ */
  category: ContactCategory;
  /** メールアドレス */
  email: string;
  /** 名前 */
  name?: string;
  /** 件名 */
  subject: string;
  /** 本文 */
  message: string;
  /** ログインユーザーID（ログイン済みの場合） */
  userId?: string;
  /** クライアントIP */
  ipAddress?: string;
  /** ユーザーエージェント */
  userAgent?: string;
  /** 作成日時（ISO 8601） */
  createdAt: string;
  /** ステータス */
  status: ContactStatus;
  /** 対応履歴 */
  actions: ContactActionEntry[];
  /** 最終対応日時（ISO 8601） */
  lastActionAt: string | null;
}

// ============================================
// API レスポンス
// ============================================

/** ページネーション情報 */
export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** 問い合わせ一覧取得結果 */
export interface ContactListResult {
  contacts: ContactRecord[];
  pagination: Pagination;
}

/** 問い合わせ統計情報 */
export interface ContactStats {
  total: number;
  byStatus: Record<ContactStatus, number>;
  byCategory: Record<ContactCategory, number>;
}

// ============================================
// インデックス用メタデータ
// ============================================

/**
 * 問い合わせインデックスメタデータ
 * KVのインデックスに保存し、一覧取得・統計計算を高速化
 */
export interface ContactIndexMeta {
  /** ステータス */
  status: ContactStatus;
  /** カテゴリ */
  category: ContactCategory;
  /** 件名 */
  subject: string;
  /** 本文（先頭100文字） */
  messageSummary: string;
  /** 名前 */
  name?: string;
  /** メールアドレス */
  email: string;
  /** 作成日時（ISO 8601） */
  createdAt: string;
  /** 最終対応日時（ISO 8601） */
  lastActionAt: string | null;
}
